import React, { useEffect } from 'react';
import { Grid, Paper, Typography, Box, LinearProgress, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchBudgets, fetchBudgetSummary } from '../slices/budgetSlice';
import { formatAmount } from '../utils/formatHelpers';
import { useLanguage } from '../contexts/LanguageContext';

// 29 ago 2026 (pedido de Augusto: "PCM tiene que enviarle una alerta por
// whatsapp y llevar un tracking en el Tablero de cómo va... si la
// persona lo ve, más fácil le es cambiar"): esto era 100% mock (datos
// hardcodeados, "Food $750 of $1000") y no estaba montado en NINGÚN
// lado del código -- ni siquiera el Tablero lo mostraba. Se reescribe
// para leer datos reales de GET /budgets/summary (vía budgetSlice) y se
// monta acá mismo... ver NotionDashboard.js.
//
// Solo se muestran las categorías más cerca de su límite (máximo 4) para
// no saturar el Tablero con el detalle completo -- el detalle completo,
// con edición, vive en /budgets. Presupuesto es SOLO en soles (ver
// AskUserQuestion del 29 ago 2026), así que no hace falta convertAndFormat
// ni ninguna tasa de cambio acá.
const MAX_CATEGORIAS_TABLERO = 4;

const BudgetProgressCards = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { language } = useLanguage();
  const summary = useSelector((state) => state.budgets.summary);
  const summaryLoading = useSelector((state) => state.budgets.summaryLoading);

  useEffect(() => {
    dispatch(fetchBudgets());
    dispatch(fetchBudgetSummary());
  }, [dispatch]);

  const getProgressColor = (gastado, presupuesto) => {
    const ratio = presupuesto > 0 ? gastado / presupuesto : 0;
    if (ratio >= 1) return 'error';
    if (ratio >= 0.8) return 'warning';
    return 'success';
  };

  const filas = [...summary]
    .map((s) => ({ ...s, ratio: s.presupuesto > 0 ? s.gastado / s.presupuesto : 0 }))
    .sort((a, b) => b.ratio - a.ratio)
    .slice(0, MAX_CATEGORIAS_TABLERO);

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{language === 'es' ? 'Presupuesto del mes' : "This month's budget"}</Typography>
        <Chip
          label={language === 'es' ? 'Gestionar presupuesto' : 'Manage budget'}
          color="primary"
          variant="outlined"
          onClick={() => navigate('/budgets')}
          sx={{ cursor: 'pointer' }}
        />
      </Box>

      {summaryLoading && filas.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {language === 'es' ? 'Cargando...' : 'Loading...'}
        </Typography>
      )}

      {!summaryLoading && filas.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {language === 'es'
            ? 'Todavía no configuraste tu presupuesto. Haz clic en "Gestionar presupuesto" para ponerle un tope mensual a tus categorías.'
            : 'You haven\'t set up your budget yet. Click "Manage budget" to cap your monthly spending by category.'}
        </Typography>
      )}

      <Grid container spacing={2}>
        {filas.map((item) => {
          const percentSpent = Math.min(100, Math.round(item.ratio * 100));
          const progressColor = getProgressColor(item.gastado, item.presupuesto);

          return (
            <Grid item xs={12} key={item.categoria}>
              <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {item.categoria}
                </Typography>
                <Typography variant="body2" color={progressColor === 'error' ? 'error' : 'text.secondary'}>
                  S/{formatAmount(item.gastado)} {language === 'es' ? 'de' : 'of'} S/{formatAmount(item.presupuesto)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: '100%', mr: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={percentSpent}
                    color={progressColor}
                    sx={{ height: 8, borderRadius: 5 }}
                  />
                </Box>
                <Box sx={{ minWidth: 40 }}>
                  <Typography variant="body2" color="text.secondary">
                    {percentSpent}%
                  </Typography>
                </Box>
              </Box>

              <Typography
                variant="caption"
                color={progressColor === 'error' ? 'error' : 'text.secondary'}
                sx={{ display: 'block', mt: 0.5 }}
              >
                {item.gastado > item.presupuesto
                  ? `S/${formatAmount(item.gastado - item.presupuesto)} ${language === 'es' ? 'sobre el presupuesto' : 'over budget'}`
                  : `S/${formatAmount(item.presupuesto - item.gastado)} ${language === 'es' ? 'restante' : 'remaining'}`}
              </Typography>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
};

export default BudgetProgressCards;
